import { useRef, useState } from 'react';
import type { AuditAction, AuditPhoto, QualificationQuestion } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Camera, Flag, ListPlus, Loader2, MessageSquare } from 'lucide-react';
import type { ResponseMap } from '../qualification-detail/types';
import { ACTION_STATUS } from '../audits/auditStatus';
import AuditPhotoThumb from './AuditPhotoThumb';

interface AuditCheckControlsProps {
  auditId: number;
  question: QualificationQuestion;
  answer?: ResponseMap[number];
  /** This check's photos. */
  photos: AuditPhoto[];
  /** Actions raised from this check's answer. */
  actions: AuditAction[];
  canEdit: boolean;
  /** Customer users raise and edit actions, also after the audit is completed. */
  canManageActions: boolean;
  /** Photos are uploading to this check. */
  uploading: boolean;
  onComment: (text: string) => void;
  onFlag: (flagged: boolean) => void;
  onAddPhotos: (files: File[]) => void;
  /** Resolves true once the photo is deleted. */
  onDeletePhoto: (photoId: number) => Promise<boolean>;
  onDownloadPhoto: (photo: AuditPhoto) => void;
  onRaiseAction: () => void;
  onOpenAction: (action: AuditAction) => void;
}

/**
 * Under each check: Finding flag, comment, photos (several; camera or gallery
 * on tablets, each deletable while the audit is open) and the actions raised
 * from it. A finding opens the comment box straight away.
 */
export default function AuditCheckControls({
  auditId, question, answer, photos, actions, canEdit, canManageActions, uploading,
  onComment, onFlag, onAddPhotos, onDeletePhoto, onDownloadPhoto, onRaiseAction, onOpenAction,
}: AuditCheckControlsProps) {
  const { formatDate } = useFormatters();
  const photoInput = useRef<HTMLInputElement>(null);
  const [commentOpen, setCommentOpen] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<AuditPhoto | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);
  const comment = answer?.reviewer_notes ?? '';
  const flagged = Boolean(answer?.flagged);
  const showComment = canEdit && (flagged || commentOpen || comment !== '');

  const confirmDelete = async () => {
    if (!confirming) return;
    setDeleting(true);
    await onDeletePhoto(confirming.id);
    setDeleting(false);
    setConfirming(null);
  };

  return (
    <div className="space-y-2 pt-1" data-testid={`audit-check-${question.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {canEdit ? (
          <Button type="button" variant={flagged ? 'destructive' : 'outline'} aria-pressed={flagged}
            onClick={() => onFlag(!flagged)} title={flagged ? 'Remove the finding' : 'Mark as a finding'}>
            <Flag className="h-4 w-4 mr-1.5" />{flagged ? 'Finding' : 'Mark finding'}
          </Button>
        ) : flagged && (
          <Badge variant="destructive" className="gap-1"><Flag className="h-3 w-3" />Finding</Badge>
        )}
        {canEdit && !showComment && (
          <Button type="button" variant="ghost" onClick={() => setCommentOpen(true)}>
            <MessageSquare className="h-4 w-4 mr-1.5" />Add comment
          </Button>
        )}
        {canEdit && question.question_type !== 'file_upload' && (
          <>
            {/* No `capture`: it forces the camera and blocks picking several from the gallery; tablets still offer the camera. */}
            <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/gif,application/pdf" multiple className="hidden"
              aria-label={`Add photos to check ${question.question_text}`}
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) onAddPhotos(files);
                e.target.value = '';
              }} />
            <Button type="button" variant="ghost" className="h-10" disabled={uploading} onClick={() => photoInput.current?.click()}>
              {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
              {uploading ? 'Uploading…' : 'Add photo(s)'}
            </Button>
          </>
        )}
        {canManageActions && (
          <Button type="button" variant="ghost" onClick={onRaiseAction}>
            <ListPlus className="h-4 w-4 mr-1.5" />Raise action
          </Button>
        )}
      </div>

      {showComment ? (
        <Textarea value={comment} rows={2} autoFocus={commentOpen && comment === ''} className="text-base"
          onChange={(e) => onComment(e.target.value)}
          placeholder={flagged ? 'What did you find? (finding comment)' : 'Comment on this check'}
          aria-label={`Comment on check ${question.question_text}`} />
      ) : !canEdit && comment && (
        <p className="rounded-md bg-muted px-3 py-2 text-sm whitespace-pre-wrap">
          <MessageSquare className="inline h-3.5 w-3.5 mr-1.5 text-muted-foreground" />{comment}
        </p>
      )}

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2" role="list" aria-label={`Photos for check ${question.question_text}`}>
          {photos.map((p) => (
            <div key={p.id} role="listitem">
              <AuditPhotoThumb auditId={auditId} photo={p} onDownload={() => onDownloadPhoto(p)}
                onDelete={canEdit ? () => setConfirming(p) : undefined} />
            </div>
          ))}
        </div>
      )}

      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {actions.map((a) => (
            <button key={a.id} type="button" onClick={() => onOpenAction(a)} disabled={!canManageActions}
              className="inline-flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-sm hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent"
              title={a.description}>
              <ListPlus className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate max-w-[20rem]">{a.description}</span>
              <span className={a.is_overdue ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}>
                {a.is_overdue ? 'Overdue' : `Due ${formatDate(a.due_date)}`}
              </span>
              <Badge className={`${ACTION_STATUS[a.status].className} border-0`}>{ACTION_STATUS[a.status].label}</Badge>
            </button>
          ))}
        </div>
      )}

      <AlertDialog open={confirming !== null} onOpenChange={(o) => { if (!o && !deleting) setConfirming(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone.{confirming ? ` (${confirming.file_name})` : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-10" disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction className="h-10 bg-red-600 hover:bg-red-700" disabled={deleting}
              onClick={(e) => { e.preventDefault(); void confirmDelete(); }}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Delete photo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
